import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CtaButtonComponent } from '../../../../../shared/components/ui/cta-button/cta-button.component';
import { InputFieldComponent } from '../../../../../shared/components/ui/input-field/input-field.component';
import { CourseService } from '../../../services/course.service';
import { StudentCompactViewComponent } from '../../student-compact-view/student-compact-view.component';
import { StudentCompactItem } from '../../../models/student.models';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-course-assignments',
  standalone: true,
  imports: [
    CommonModule,
    CtaButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    InputFieldComponent,
    StudentCompactViewComponent,
  ],
  templateUrl: './course-assignments.component.html',
  styleUrl: './course-assignments.component.scss',
})
export class CourseAssignmentsComponent {
  #courseService = inject(CourseService);
  #translate = inject(TranslateService);
  courseId = input<string | undefined>(undefined);

  stepCompleted = output<void>();

  inviteCodeControl = new FormControl('');
  addError = signal<string | null>(null);
  isAdding = signal(false);
  currentPage = signal(1);

  /** Students enrolled in this course (from my-students filtered by courseId) */
  assignedStudents = computed(() => {
    const cid = this.courseId();
    if (!cid) return [];
    const all = this.#courseService.enrolledStudents();
    return all.filter((s) =>
      s.courseProgress?.some((p) => p.courseId === cid),
    );
  });

  /** Map to StudentCompactItem for StudentCompactView (with userId for remove) */
  studentItems = computed<Array<StudentCompactItem & { userId: string }>>(() => {
    const cid = this.courseId();
    const students = this.assignedStudents();
    if (!cid) return [];
    return students.map((s) => {
      const progress = s.courseProgress?.find((p) => p.courseId === cid);
      const pct = progress?.completionPercent ?? 0;
      return {
        name: this.displayName(s),
        coursesLabel: progress?.courseName ?? '',
        percentage: pct,
        ratingBadge: `${pct}%`,
        avatarUrl: '',
        userId: s.userId,
      };
    });
  });

  /** Paginated slice - first 10 of current page */
  paginatedStudents = computed(() => {
    const items = this.studentItems();
    const page = this.currentPage();
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  });

  totalPages = computed(() =>
    Math.max(1, Math.ceil(this.studentItems().length / PAGE_SIZE)),
  );

  addUserByCode = this.#courseService.addUserByInviteCode;
  removeUser = this.#courseService.removeUserFromCourse;

  onAddStudent(): void {
    const code = (this.inviteCodeControl.value ?? '').trim().toUpperCase();
    const cid = this.courseId();
    if (!code || code.length !== 6) {
      this.addError.set(
        this.#translate.instant(
          'app.components.courseCreator.assign.errors.codeRequired',
        ),
      );
      return;
    }
    if (!cid) {
      this.addError.set(
        this.#translate.instant(
          'app.components.courseCreator.assign.errors.courseNotFound',
        ),
      );
      return;
    }
    this.addError.set(null);
    this.isAdding.set(true);
    this.addUserByCode.mutate(
      { inviteCode: code, courseId: cid },
      {
        onSuccess: () => {
          this.inviteCodeControl.setValue('');
          this.isAdding.set(false);
        },
        onError: (err: any) => {
          const status = err?.status ?? err?.error?.statusCode;
          const msg =
            status === 404
              ? this.#translate.instant(
                  'app.components.courseCreator.assign.errors.userNotFound',
                )
              : err?.error?.message ??
                err?.message ??
                this.#translate.instant(
                  'app.components.courseCreator.assign.errors.addFailed',
                );
          this.addError.set(msg);
          this.isAdding.set(false);
        },
      },
    );
  }

  onRemoveStudent(userId: string): void {
    const cid = this.courseId();
    if (!cid) return;
    this.removeUser.mutate({ userId, courseId: cid });
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.max(1, Math.min(page, this.totalPages())));
  }

  onComplete(): void {
    this.stepCompleted.emit();
  }

  displayName(student: {
    userName: string;
    personalData?: { firstName?: string; lastName?: string };
  }): string {
    const fn = (student as any).personalData?.firstName;
    const ln = (student as any).personalData?.lastName;
    if (fn || ln) return [fn, ln].filter(Boolean).join(' ').trim();
    return student.userName;
  }

}
